import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Div } from '../../layouts';
import { CMS } from '../../utils/CMS';
import { Dropdown } from '../../components/Dropdown/Dropdown';
import { PaxSelectorDesktopEl } from './PaxSelectorDesktop.style';
import { ConnectedPaxSelectorContent } from './Content';
import { TrainConnectedPaxSelectorContent } from './TrainContent';
import { totalPax, totalTrainPax } from '../../redux/currentSearch/selectors';
import { TrackingUtils } from '../../utils';
import { EntryPoint, TrainPaxErrorsProps, TrainPaxErrorsPropsDefaults } from '../../models';
import buttonClassNames from '../SearchButton/buttonClassNames';
import { ButtonDefault } from '../../components';

const getPaxLabel = (shouldUseTrainPaxInLabel, pax) => {
    if (shouldUseTrainPaxInLabel) {
        return <CMS id="paxSelector.trains.passengers" values={{ count: pax }} />;
    }
    return <CMS id="flightsManager.passengers" values={{ count: pax }} />;
};

const shouldShowErrorState = (showErrors, hasSubmittedPax, hasErrors) => (showErrors || hasSubmittedPax) && hasErrors;

export const PaxSelectorDesktop = ({ pax, disabled, activeProduct, trainPaxErrors, showErrors, trainPaxObj }) => {
    const { t } = useTranslation();
    const node = React.useRef(null);
    const [isOpen, setOpen] = React.useState(false);
    const [hasSubmittedPax, setHasSubmittedPax] = React.useState(false);
    const shouldUseTrainContent = activeProduct === EntryPoint.TRAINS;
    const PaxSelectorContent = shouldUseTrainContent ? TrainConnectedPaxSelectorContent : ConnectedPaxSelectorContent;
    const shouldUseTrainPaxInLabel = trainPaxObj?.youth === 1 && pax === 1 && shouldUseTrainContent;

    // Extract error existence check to avoid duplication
    const hasValidationErrors = trainPaxErrors?.paxSelectorLevelError || trainPaxErrors?.paxAgeFieldLevelError;
    const hasTrainPaxErrors = shouldUseTrainContent && !!hasValidationErrors;
    const showInvalidState = shouldShowErrorState(showErrors, hasSubmittedPax, hasTrainPaxErrors);

    const arrowClassName = classNames({
        'odf-input-icon odf-icon': true,
        'odf-icon-arrow-up odf-z-index-3': isOpen,
        'odf-icon-arrow-down': !isOpen,
    });
    const paxSelectorDesktopClassNames = classNames({
        'odf-space-inner-top-m odf-space-inner-left-l odf-space-inner-right-l odf-space-inner-bottom-m ': true,
        'odf-box odf-box-layer odf-popup odf-popup-flex opened': true,
    });

    const inputClassNames = classNames({
        'odf-input odf-text-lg odf-input-m odf-input-inline odf-input-inline-last odf-select odf-space-outer-right-m': true,
        disabled,
    });
    const wrapperClassNames = classNames({
        'focus odf-input-inline-focus': isOpen,
        'odf-input-group': true,
        'invalid odf-input-inline-invalid': showInvalidState,
    });

    const close = React.useCallback(() => {
        if (hasTrainPaxErrors) {
            // Show validation errors instead of closing
            setHasSubmittedPax(true);
            return;
        }
        setOpen(false);
    }, [hasTrainPaxErrors]);

    const handlePaxClick = () => {
        if (disabled || isOpen) {
            setOpen(false);
            return;
        }

        if (TrackingUtils.trackEvent && (activeProduct === EntryPoint.FLIGHTS || activeProduct === EntryPoint.TRAINS)) {
            const action = activeProduct === EntryPoint.FLIGHTS ? 'searcher_flights' : 'searcher_trains';
            TrackingUtils.trackEvent({
                action,
                label: 'edit_search_click_open_pass',
            });
        }

        setOpen(true);
    };

    useEffect(() => {
        if (!hasTrainPaxErrors) setHasSubmittedPax(false);
    }, [hasTrainPaxErrors]);

    return (
        <div ref={node} data-testid="pax-selector-wrapper">
            <div className={wrapperClassNames} onClick={handlePaxClick}>
                <Div className={inputClassNames} data-testid="pax-selector-summary" whiteSpace="nowrap">
                    {getPaxLabel(shouldUseTrainPaxInLabel, pax)}
                </Div>
                <span className={arrowClassName} />
            </div>
            <Dropdown open={isOpen} onClose={() => setOpen(false)} parentRef={node}>
                <PaxSelectorDesktopEl
                    data-testid="pax-selector-picker"
                    className={paxSelectorDesktopClassNames}
                >
                    <Div width="270px">
                        <PaxSelectorContent hasSubmittedPax={hasSubmittedPax} />
                    </Div>
                    <div className="odf-stroke-primary" />
                    <div className="odf-input-group odf-space-inner-top-m">
                        <ButtonDefault
                            className={`odf-col-span12 ${buttonClassNames('primary', false, false)}`}
                            onClick={close}
                            disabled={!!(hasSubmittedPax && hasTrainPaxErrors)}
                        >{t('search.button.done')}
                        </ButtonDefault>
                    </div>
                </PaxSelectorDesktopEl>
            </Dropdown>
            {showInvalidState && (
                <div
                    className="odf-label odf-text-sm odf-space-outer-xs odf-space-inner-left-xs"
                    style={{ color: 'var(--semantic-color-negative, #d32f2f)' }}
                >
                    {trainPaxErrors.paxSelectorLevelError || trainPaxErrors.paxAgeFieldLevelError}
                </div>
            )}
        </div>
    );
};

PaxSelectorDesktop.propTypes = {
    pax: PropTypes.number.isRequired,
    disabled: PropTypes.bool,
    activeProduct: PropTypes.string,
    trainPaxObj: PropTypes.object,
    trainPaxErrors: TrainPaxErrorsProps,
    showErrors: PropTypes.bool,
};

PaxSelectorDesktop.defaultProps = {
    disabled: false,
    activeProduct: EntryPoint.FLIGHTS,
    trainPaxObj: {},
    trainPaxErrors: TrainPaxErrorsPropsDefaults,
    showErrors: false,
};

const mapStateToProps = (state) => {
    const { activeProduct } = state.activeProduct;
    const isRailsSearcher = activeProduct === EntryPoint.TRAINS;
    const shouldUseTrainPax = isRailsSearcher;

    return {
        pax: shouldUseTrainPax ? totalTrainPax(state) : totalPax(state),
        activeProduct,
        trainPaxObj: state.currentSearch.trainPax || {},
        trainPaxErrors: state.currentSearch.trainPaxErrors || {},
        showErrors: state.currentSearch.segments?.some((segment) => segment.showErrors) || false,
    };
};

export const ConnectedPaxSelectorDesktop = connect(mapStateToProps)(PaxSelectorDesktop);
